import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { SlotHoldService } from '../../service/slot-hold.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { CourtSlotBookingRequestDto } from '../booking-container/booking-container.component';
import { AuthService } from '../../auth/components/auth.service';
import { User } from '../../auth/shared/UserInterface';
import { AuthMode } from '../../auth/shared/auth-form.component';
import { BookingApiService } from '../../service/booking-api.service';
import { CreateBookingRequest, PaymentMethod } from '../../models/booking.model';
import { TimeUtilsService } from '../../service/time-utils.service';
import { ToastService } from '../../shared/services/toast.service';
import { PaymentSectionComponent } from '../../shared/components/payment-section/payment-section.component';
import { CheckoutPaymentMethod } from '../booking-modal/payment/payment-options/payment-options.component';
import { VoucherCalculation } from '../../shared/components/payment-section/voucher-calculation.interface';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { IconType } from '../../shared/services/icon.service';
import { Router } from '@angular/router';
import { formatDateLongGerman } from '../../shared/utils/date-format.util';
import { OrganizationSettingsService } from '../../shared/services/organization-settings.service';
import { PlayerSelectorComponent, SelectedPlayer, GuestAllowanceHint } from '../booking-container/player-selector/player-selector.component';
import { PricingService, MultiPlayerPriceResponse } from '../../shared/services/pricing.service';
import { GuestAllowanceService, RemainingAllowanceResponse } from '../../shared/services/guest-allowance.service';
import { ErrorHandlingService } from '../../shared/services/error-handling.service';
import { VenueTimezoneService } from '../../service/venue-timezone.service';

// Step type for multi-step booking flow
type BookingStep = 'players' | 'preview' | 'payment' | 'success';

interface ConfirmedBookingData {
  categoryName: string;
  courtName: string;
  date: Date;
  startTime: string;
  price: number;
  players?: SelectedPlayer[];
}

/**
 * BookingModalComponent - Main component for court booking and payment processing
 *
 * This component handles the complete booking flow:
 * 1. Displays booking details from a held court slot
 * 2. Delegates payment/voucher handling to PaymentSectionComponent
 * 3. Confirms bookings with the backend
 *
 * Key Features:
 * - Real-time countdown timer for booking hold expiration (5 minutes)
 * - Shared payment section for vouchers and Stripe integration
 */
@Component({
  selector: 'app-booking-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PaymentSectionComponent,
    IconComponent,
    PlayerSelectorComponent,
  ],
  templateUrl: './booking-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-modal.component.css'],
})
export class BookingModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() bookingConfirmed = new EventEmitter<void>();
  @Output() modalClosed = new EventEmitter<void>();

  heldSlot: CourtSlotBookingRequestDto | null = null;
  countdownDisplay: string = '';
  bookingInProgress = false;
  bookingError: string | null = null;

  // Payment state (managed by PaymentSectionComponent, we track for submit logic)
  /**
   * The method payment-options reported, or null until it has reported one
   * (e.g. while the SEPA mandate is still loading). Null keeps submit disabled
   * — it must never be read as "cash".
   *
   * This and the other payment-step fields below are SIGNALS because the child
   * emits them from its own lifecycle hooks (ngOnInit / ngOnChanges), i.e.
   * while this component's template is being checked. A plain field changed at
   * that point trips NG0100 on every binding that reads it; a signal write
   * marks the view for a fresh pass instead.
   */
  paymentMethod = signal<CheckoutPaymentMethod | null>(null);
  stripePaymentIntentId: string | null = null;
  selectedVoucherCodes: string[] = [];
  voucherCalculation: VoucherCalculation | null = null;
  isFullyCovered = false;

  /** PayPal renders its own submit button, so our CTA must step aside. */
  providerOwnsSubmit = signal(false);
  /** A voucher deduction is being validated — see PaymentSectionComponent.voucherCalculationPendingChanged. */
  voucherCalculationPending = signal(false);

  // Stripe Payment Element state
  private stripeConfirmFn = signal<(() => Promise<void>) | null>(null);
  stripeFormComplete = signal(false);

  // HTTPS detection for progressive enhancement
  isHttpsAvailable = false;

  // Organization settings (for payment toggles)
  onSitePaymentEnabled = false;
  stripePaymentEnabled = false;
  sepaPaymentEnabled = false;
  paypalPaymentEnabled = false;
  paypalClientId: string | null = null;
  paypalEnvironment: 'sandbox' | 'live' | null = null;
  voucherPaymentEnabled = false;

  AuthMode = AuthMode;
  IconType = IconType;

  // Multi-step flow state
  step = signal<BookingStep>('players');

  // Player selection state (moved from BookingContainerComponent)
  selectedPlayers: SelectedPlayer[] = [];

  /**
   * Determines if players step should be shown.
   * Note: This is a method, not a computed, because heldSlot is a plain property
   * (not a signal), so a computed would only evaluate once at initialization.
   *
   * Shows players step when:
   * - maxPlayers > 1 (allows adding players)
   * - OR pricingPerPlayer is enabled (shows guest slots even without adding players)
   */
  showPlayersStep(): boolean {
    const maxPlayers = this.getMaxPlayers();
    const pricingPerPlayer = this.isPricingPerPlayerEnabled();
    return maxPlayers > 1 || pricingPerPlayer;
  }

  // Captured booking data for success screen (decoupled from heldSlot)
  confirmedBooking: ConfirmedBookingData | null = null;

  private subscriptions: Subscription = new Subscription();
  private hasLoadedSlot = false;

  courtLoading = true;
  alternativeCourtSelected = false;

  private timeRemaining: number = 300;
  private totalHoldDuration: number = 300;

  private unloadHandler = () => {
    this.slotHoldService.releaseHeldSlot();
  };

  private holdReleased = false;

  // Multi-player pricing preview state
  multiPlayerPricePreview: MultiPlayerPriceResponse | null = null;
  pricePreviewLoading = false;
  extraGuestSlots = 0; // extra guest slots added by user beyond minPlayers
  private pricingSubscription: Subscription | null = null;

  // Guest allowance (member-only bookings): remaining units + fee of the
  // configs matching the authenticated user's memberships for this category.
  guestAllowanceInfo: RemainingAllowanceResponse | null = null;
  // Precomputed selector hint (stable reference for the OnPush child input).
  guestAllowanceHint: GuestAllowanceHint | null = null;

  constructor(
    private slotHoldService: SlotHoldService,
    private bookingApiService: BookingApiService,
    public authService: AuthService,
    private timeUtils: TimeUtilsService,
    private toastService: ToastService,
    private router: Router,
    private organizationSettingsService: OrganizationSettingsService,
    private pricingService: PricingService,
    private errorHandlingService: ErrorHandlingService,
    private venueTimezone: VenueTimezoneService,
    private guestAllowanceService: GuestAllowanceService,
  ) {}

  ngOnInit(): void {
    // Get the currently held slot
    this.subscriptions.add(
      this.slotHoldService.getHeldSlot().subscribe((slot) => {
        if (!this.heldSlot && slot) {
          this.hasLoadedSlot = true;
          this.courtLoading = false;

          // Set initial step based on category's max players and pricing mode
          const maxPlayers = (slot.category as { maxPlayers?: number })?.maxPlayers ?? 4;
          const pricingPerPlayer = (slot.category as { pricingPerPlayer?: boolean })?.pricingPerPlayer ?? false;
          if (maxPlayers > 1 || pricingPerPlayer) {
            this.step.set('players');
          } else {
            this.step.set('preview');
          }

          // Fetch organization settings when we have a slot
          const orgId = (slot.category as { organizationId?: string })?.organizationId;
          if (orgId) {
            this.loadOrganizationSettings(orgId);
            this.loadGuestAllowance(orgId, slot);
          }
        } else if (
          this.heldSlot &&
          slot &&
          this.heldSlot.court?.id !== slot.court?.id
        ) {
          this.courtLoading = false;
        }

        const previousSlot = this.heldSlot;
        this.heldSlot = slot;

        // Only auto-close if we're not on success screen (allow hold expiry during booking)
        if (!slot && this.hasLoadedSlot && this.step() !== 'success') {
          this.close.emit();
        }

        if (!slot) {
          // Slot cleared — reset all player/pricing/voucher state
          this.pricingSubscription?.unsubscribe();
          this.pricePreviewLoading = false;
          this.selectedPlayers = [];
          this.multiPlayerPricePreview = null;
          this.extraGuestSlots = 0;
          this.resetVoucherState();
        } else if (previousSlot && previousSlot.id !== slot.id) {
          // Slot identity changed (e.g. alternative court) — clear pricing & voucher state
          // Keep selectedPlayers since they're still valid for the same category
          this.pricingSubscription?.unsubscribe();
          this.pricePreviewLoading = false;
          this.multiPlayerPricePreview = null;
          this.resetVoucherState();
        }
      })
    );

    // Hold acquisition failures (e.g. 409 when every court is blocked by a
    // course) never emit a heldSlot, so courtLoading would spin forever.
    // Surface the error and close the modal instead. skip(1) ignores the
    // BehaviorSubject's replayed value, which could be a stale error from a
    // previous attempt.
    this.subscriptions.add(
      this.slotHoldService.getError().pipe(skip(1)).subscribe((error) => {
        if (error && !this.heldSlot) {
          this.toastService.error(error);
          this.close.emit();
        }
      })
    );

    // Get the countdown timer
    this.subscriptions.add(
      this.slotHoldService.getFormattedTimeRemaining().subscribe((time) => {
        this.countdownDisplay = time;
      })
    );

    // Get alternative court notification state
    this.subscriptions.add(
      this.slotHoldService
        .getAlternativeCourtSelected()
        .subscribe((isAlternative) => {
          this.alternativeCourtSelected = isAlternative;
        })
    );

    // Subscribe to totalHoldDuration and timeRemaining
    this.subscriptions.add(
      this.slotHoldService.getTotalHoldDuration().subscribe((duration) => {
        this.totalHoldDuration = duration;
      })
    );

    this.subscriptions.add(
      this.slotHoldService.getTimeRemaining().subscribe((remaining) => {
        this.timeRemaining = remaining;
      })
    );

    // Add window unload handler
    window.addEventListener('beforeunload', this.unloadHandler);

    // Check if we're on HTTPS
    this.isHttpsAvailable = window.location.protocol === 'https:';
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.unloadHandler);

    if (this.heldSlot && !this.holdReleased) {
      this.slotHoldService.releaseHeldSlot();
    }

    this.pricingSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.heldSlot = null;
  }

  getCurrentUser(): User | null | undefined {
    return this.authService.currentUserSignal();
  }

  onCancel(): void {
    try {
      this.slotHoldService.releaseHeldSlot();
    } catch {
      // Slot hold release is best-effort; ignore errors
    }

    this.holdReleased = true;
    this.modalClosed.emit();
    this.close.emit();
  }

  // ============================================================================
  // Payment Section Event Handlers
  // ============================================================================

  onPaymentMethodChanged(method: CheckoutPaymentMethod | null): void {
    this.paymentMethod.set(method);
    // Reset Stripe state so confirm button doesn't stay enabled with stale callback
    this.stripeConfirmFn.set(null);
    this.stripeFormComplete.set(false);
  }

  onPaymentSuccess(event: { paymentIntentId: string }): void {
    // Route on the SELECTED METHOD, not on the shape of the id.
    //
    // This used to test `paymentIntentId.startsWith('pi_')` and treat anything
    // else as "vouchers covered everything". A PayPal order id does not start
    // with `pi_`, so a completed PayPal payment would have been silently
    // rerouted into the voucher-only path — booking the court a second time,
    // for free, after the buyer had already paid.
    if (this.paymentMethod() === 'paypal') {
      // Nothing left to confirm: the capture endpoint already created the
      // booking server-side inside the same transaction as the payment.
      this.completeBookingAfterPaypalPayment();
      return;
    }

    if (this.isFullyCovered || !event.paymentIntentId) {
      this.processUnifiedBooking(PaymentMethod.VOUCHER);
      return;
    }

    this.stripePaymentIntentId = event.paymentIntentId;
    this.completeBookingAfterStripePayment(event.paymentIntentId);
  }

  /** True while PayPal renders its own submit button (see PaymentSection). */
  onProviderOwnsSubmit(owns: boolean): void {
    this.providerOwnsSubmit.set(owns);
  }

  /**
   * Whether PayPal currently owns the submit, i.e. whether our CTA must step
   * aside. Only while PayPal's own button is actually on screen: once vouchers
   * cover the whole amount, payment-section unmounts payment-options — PayPal
   * button included — without any further emission, so the last
   * `providerOwnsSubmit(true)` would otherwise leave the member with no submit
   * control at all. Unticking a voucher remounts payment-options, which
   * re-emits its selection from scratch.
   */
  isSubmitOwnedByProvider(): boolean {
    return this.providerOwnsSubmit() && !this.isFullyCoveredByVouchers();
  }

  onPaymentError(event: { error: string }): void {
    this.bookingError = event.error;
    this.bookingInProgress = false;
    // Async failure → actionable feedback, consistent with the other error
    // paths in this modal (the inline banner alone is easy to miss).
    this.toastService.error(event.error);
  }

  onConfirmFnReady(fn: () => Promise<void>): void {
    this.stripeConfirmFn.set(fn);
  }

  onPaymentFormComplete(complete: boolean): void {
    this.stripeFormComplete.set(complete);
  }

  onVoucherCalculationChanged(calculation: VoucherCalculation | null): void {
    this.voucherCalculation = calculation;
  }

  onFullyCoveredByVouchersChanged(isCovered: boolean): void {
    this.isFullyCovered = isCovered;
  }

  onSelectedVoucherCodesChanged(codes: string[]): void {
    this.selectedVoucherCodes = codes;
  }

  // ============================================================================
  // Booking Completion
  // ============================================================================

  /**
   * PayPal has already been captured AND the booking committed server-side by
   * `POST /payments/paypal/orders/:id/capture`. There is no confirmation call to
   * make here — doing one would risk creating a second booking.
   */
  private completeBookingAfterPaypalPayment(): void {
    this.bookingInProgress = false;
    this.holdReleased = true;
    this.toastService.success('Zahlung erfolgreich! Buchung bestätigt.');

    this.confirmedBooking = {
      categoryName: this.heldSlot!.category.name,
      courtName: this.heldSlot!.court.name,
      date: this.heldSlot!.date,
      startTime: this.heldSlot!.startTime,
      price: this.getEffectiveTotalPrice(),
      players:
        this.selectedPlayers.length > 0 ? [...this.selectedPlayers] : undefined,
    };
    this.step.set('success');
    this.bookingConfirmed.emit();
  }

  private async completeBookingAfterStripePayment(
    paymentIntentId: string
  ): Promise<void> {
    try {
      this.bookingInProgress = true;
      const confirmResponse = await firstValueFrom(
        this.bookingApiService.confirmExpressCheckout({
          bookingHoldId: this.heldSlot!.id,
          stripePaymentIntentId: paymentIntentId,
        })
      );

      if (!confirmResponse) {
        throw new Error('Failed to confirm booking');
      }

      this.bookingInProgress = false;
      this.holdReleased = true;
      this.toastService.success('Zahlung erfolgreich! Buchung bestätigt.');

      // Capture booking data before transitioning to success
      this.confirmedBooking = {
        categoryName: this.heldSlot!.category.name,
        courtName: this.heldSlot!.court.name,
        date: this.heldSlot!.date,
        startTime: this.heldSlot!.startTime,
        price: this.getEffectiveTotalPrice(),
        players: this.selectedPlayers.length > 0 ? [...this.selectedPlayers] : undefined,
      };
      this.step.set('success');
      this.bookingConfirmed.emit();
    } catch (error: unknown) {
      this.bookingInProgress = false;
      this.bookingError = this.errorHandlingService.getGeneralErrorMessage(error);
      this.toastService.error(`Buchung fehlgeschlagen: ${this.bookingError}`);
    }
  }

  // ============================================================================
  // Timer Methods
  // ============================================================================

  calculateProgress(): number {
    if (!this.totalHoldDuration || !this.timeRemaining) return 100;
    return Math.max(
      0,
      Math.min(100, (this.timeRemaining / this.totalHoldDuration) * 100)
    );
  }

  getTimerClass(): string {
    if (this.timeRemaining < 60) {
      return 'danger';
    } else if (this.timeRemaining < 120) {
      return 'warning';
    }
    return '';
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.step() === 'success') {
      this.bookAnother();
    } else if (this.step() === 'payment') {
      this.goBackToPreview();
    } else if (this.step() === 'preview' && this.showPlayersStep()) {
      this.goBackToPlayers();
    } else {
      this.onCancel();
    }
  }

  // ============================================================================
  // Multi-Step Navigation
  // ============================================================================

  /**
   * Check if can proceed from players step (minimum player count met)
   */
  canProceedFromPlayers(): boolean {
    if (!this.heldSlot) return false;
    const minPlayers = (this.heldSlot.category as { minPlayers?: number })?.minPlayers ?? 1;
    const filledSlots = this.selectedPlayers.length + 1; // +1 for organizer
    if (this.isPricingPerPlayerEnabled()) {
      // In per-player mode, guest slots auto-fill to at least minPlayers.
      // Required guests = max(0, minPlayers - filledSlots), plus any extra added by user.
      // So total is always >= minPlayers when pricingPerPlayer is enabled.
      return true;
    }
    // Standard mode: only organizer + registered players count
    return filledSlots >= minPlayers;
  }

  /**
   * Navigate from players to preview step
   * Also triggers multi-player pricing calculation if players are selected
   */
  goToPreview(): void {
    this.loadMultiPlayerPricing();
    this.step.set('preview');
  }

  /**
   * Load multi-player pricing breakdown from the backend.
   * Called when navigating to preview step with multiple players.
   *
   * When pricingPerPlayer is enabled:
   * - Guest slots are pre-filled to minPlayers, optionally added up to maxPlayers
   * - guestSlotCount computed from minPlayers + extraGuestSlots
   * - Guest slots are priced at default rate
   */
  private loadMultiPlayerPricing(): void {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.heldSlot) {
      return;
    }

    const organizationId = this.getOrganizationId();
    const categoryId = this.getCategoryId();

    if (!organizationId || !categoryId) {
      return;
    }

    // Check if per-player pricing is enabled
    const pricingPerPlayer = this.isPricingPerPlayerEnabled();

    // Skip only when neither per-player pricing, nor co-players, nor guest
    // slots (guest allowance in booker-pays mode) are in play.
    if (
      !pricingPerPlayer &&
      this.selectedPlayers.length === 0 &&
      this.getGuestSlotCount() === 0
    ) {
      this.multiPlayerPricePreview = null;
      return;
    }

    // Build player list: current user (organizer) + selected players
    const allPlayerIds = [currentUser.userId, ...this.selectedPlayers.map(p => p.id)];

    // Use actual guest slot count from player-selector (tracks minPlayers + extras)
    const guestSlotCount = this.getGuestSlotCount();

    // Format date as YYYY-MM-DD (using TimeUtilsService to avoid timezone shift)
    const bookingDateStr = this.timeUtils.formatDateForApi(new Date(this.heldSlot.date));

    // Extract HH:mm in venue local time (pricing rules use venue wall-clock time)
    const bookingTimeHHmm = this.venueTimezone.formatTime(this.heldSlot.startTime);

    this.pricePreviewLoading = true;

    const request = {
      organizationId,
      categoryId,
      playerUserIds: allPlayerIds,
      guestSlotCount: guestSlotCount > 0 ? guestSlotCount : undefined,
      durationMinutes: this.heldSlot.durationMinutes,
      bookingDate: bookingDateStr,
      bookingTime: bookingTimeHHmm,
      pricingPerPlayer: this.isPricingPerPlayerEnabled(),
    };

    // Cancel any in-flight pricing request to prevent stale data
    this.pricingSubscription?.unsubscribe();

    this.pricingSubscription = this.pricingService.calculateMultiPlayerPrice(request).subscribe({
      next: (result) => {
        this.multiPlayerPricePreview = result;
        this.pricePreviewLoading = false;
      },
      error: (httpError) => {
        this.multiPlayerPricePreview = null;
        this.pricePreviewLoading = false;
        // Guest-allowance rejections carry actionable bilingual messages
        // (remaining units, reset date, no-combining explanation) — surface
        // them instead of a generic warning.
        if (
          this.errorHandlingService.hasErrorCode(httpError, 'GUEST_ALLOWANCE_EXHAUSTED') ||
          this.errorHandlingService.hasErrorCode(httpError, 'GUEST_ALLOWANCE_NOT_CONFIGURED') ||
          this.errorHandlingService.hasErrorCode(httpError, 'MULTI_PLAYER_GUESTS_NOT_SUPPORTED')
        ) {
          this.toastService.error(
            this.errorHandlingService.getGeneralErrorMessage(httpError)
          );
        } else {
          this.toastService.warning('Preisberechnung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
      }
    });
  }

  /**
   * Check if per-player pricing mode is enabled for this category
   */
  isPricingPerPlayerEnabled(): boolean {
    return (this.heldSlot?.category as { pricingPerPlayer?: boolean })?.pricingPerPlayer ?? false;
  }

  /**
   * Load the authenticated user's remaining guest allowance FOR THIS SLOT.
   *
   * The read is window-scoped on purpose: the guest fee + quota only apply on
   * member-only windows (Decision 6 / Step 12 of the membership-only pricing
   * design), and the backend answers `appliesToWindow` with the very analysis
   * it prices with. A category-scoped read used to drive the hint here and
   * promised "Gastgebühr … noch N Gasteinheiten" on standard-priced windows
   * where neither fee nor quota ever fired
   * (docs/tickets/fixed_2026-08-17_guest-quota-not-consumed.md). Silently absent when the
   * category has no configs, the user holds no matching membership, or the
   * window is standard-priced.
   */
  private loadGuestAllowance(orgId: string, slot: CourtSlotBookingRequestDto): void {
    const categoryId = slot.category.id;
    if (!categoryId || !this.getCurrentUser()) {
      return;
    }
    const dateStr = this.timeUtils.formatDateForApi(slot.date);
    this.subscriptions.add(
      this.guestAllowanceService
        .getRemaining(orgId, categoryId, dateStr, {
          startTime: slot.startTime,
          durationMinutes: slot.durationMinutes,
        })
        .subscribe({
        next: (info) => {
          this.guestAllowanceInfo = info;
          this.guestAllowanceHint = this.computeGuestAllowanceHint(info);
        },
        error: () => {
          // Non-blocking enrichment — booking works without the hint; the
          // backend enforces quota/fee rules authoritatively at quote time.
          this.guestAllowanceInfo = null;
          this.guestAllowanceHint = null;
        },
      })
    );
  }

  /**
   * The hint passed to the player selector: fee + remaining units of the
   * config the backend would select (lowest fee among configs with capacity;
   * falls back to the lowest-fee config when everything is exhausted so the
   * exhausted state is still displayed). Null when no config matches.
   * Computed once per allowance load (not in a template getter) so the
   * OnPush player selector keeps a stable input reference.
   */
  private computeGuestAllowanceHint(
    info: RemainingAllowanceResponse | null
  ): GuestAllowanceHint | null {
    // Only a window the allowance APPLIES to gets the hint (and the guest cap
    // that comes with it). `null` = no window asked → treated as "does not
    // apply", never as "applies".
    if (!info || info.appliesToWindow !== true || info.configs.length === 0) {
      return null;
    }
    const byFee = [...info.configs].sort(
      (a, b) => a.guestFeePerHourCents - b.guestFeePerHourCents
    );
    const selected = byFee.find((c) => c.remaining > 0) ?? byFee[0];
    // Aggregate best remaining across configs (units are never combined, but
    // the best single config is what the member can actually use next).
    const bestRemaining = Math.max(...info.configs.map((c) => c.remaining));
    return {
      guestFeePerHour: selected.guestFeePerHourCents / 100,
      remaining: selected.remaining > 0 ? selected.remaining : bestRemaining,
      year: info.year,
      resetDate: info.resetDate,
    };
  }

  /**
   * True when a guest allowance config applies to this user AND to the held
   * window (member-only). On a standard-priced window this is false even if
   * the category has configs — the backend would refuse guest slots there in
   * booker-pays mode (MULTI_PLAYER_GUESTS_NOT_SUPPORTED) and price them at the
   * plain share in per-player mode, so no allowance UI must be offered.
   */
  hasGuestAllowance(): boolean {
    return (
      this.guestAllowanceInfo?.appliesToWindow === true &&
      (this.guestAllowanceInfo?.configs.length ?? 0) > 0
    );
  }

  /**
   * Check if multi-player pricing is required but not yet available.
   * Used to block navigation to payment until pricing is loaded.
   */
  isMultiPlayerPricingPending(): boolean {
    if (this.pricePreviewLoading) return true;
    if (this.isPricingPerPlayerEnabled() && !this.multiPlayerPricePreview) return true;
    if (this.selectedPlayers.length > 0 && !this.multiPlayerPricePreview) return true;
    // Guest slots in booker-pays mode carry guest fees — the quote is the
    // price authority, so payment must wait for it.
    if (this.getGuestSlotCount() > 0 && !this.multiPlayerPricePreview) return true;
    return false;
  }

  /**
   * Navigate from preview to payment step, or directly confirm if vouchers cover everything
   */
  goToPaymentOrConfirm(): void {
    if (this.isMultiPlayerPricingPending()) return;

    if (
      !this.nothingToCharge() &&
      !this.onSitePaymentEnabled &&
      !this.stripePaymentEnabled &&
      !this.sepaPaymentEnabled &&
      !this.paypalPaymentEnabled &&
      !this.voucherPaymentEnabled
    ) {
      // No payment method available at all — nothing to do in the payment step.
      // A 0,00 € booking needs no method, so it is never stopped here.
      this.toastService.warning('Keine Zahlungsmethode verfügbar. Bitte kontaktieren Sie den Anbieter.');
    } else {
      // Voucher selection lives in the payment step. The payment-section
      // instance is recreated on (re-)entry with nothing selected, so reset
      // the modal-side voucher and payment-method state to keep both in sync.
      this.resetVoucherState();
      this.resetPaymentMethodState();
      this.step.set('payment');
    }
  }

  /**
   * Clears the modal-side mirror of the payment-section's voucher state.
   * Must be called whenever the payment-step component instance is (about to
   * be) recreated, so stale codes can't be submitted invisibly.
   */
  private resetVoucherState(): void {
    this.selectedVoucherCodes = [];
    this.voucherCalculation = null;
    this.isFullyCovered = false;
  }

  /**
   * Clears the modal-side mirror of the payment-options state (selected
   * method, PayPal CTA hand-off, Stripe confirm state). A freshly mounted
   * payment-options has decided nothing yet and only reports once it has, so
   * a previous visit's method — or a previous PayPal selection hiding the
   * CTA — must not survive into the new instance.
   */
  private resetPaymentMethodState(): void {
    this.paymentMethod.set(null);
    this.voucherCalculationPending.set(false);
    this.providerOwnsSubmit.set(false);
    this.stripeConfirmFn.set(null);
    this.stripeFormComplete.set(false);
  }

  /**
   * Navigate back to players step
   */
  goBackToPlayers(): void {
    this.step.set('players');
  }

  /**
   * Navigate back to preview step
   */
  goBackToPreview(): void {
    // Reset payment-method and Stripe state since the payment step (and the
    // Payment Element with it) will be destroyed
    this.resetPaymentMethodState();
    this.stripePaymentIntentId = null;
    // Also drop the voucher state: the payment-step component instance (and
    // with it the visible selection) is destroyed, so keeping the codes would
    // desync the preview button label from what re-entering the step shows.
    this.resetVoucherState();
    this.step.set('preview');
  }

  /**
   * Handle player selection changes from PlayerSelectorComponent
   */
  onPlayersChanged(players: SelectedPlayer[]): void {
    this.selectedPlayers = players;
  }

  /**
   * Get the effective total price, considering multi-player pricing if available.
   * Falls back to heldSlot.price for single-player bookings.
   *
   * When pricingPerPlayer is enabled, always use the multi-player calculated price
   * (includes guest slots at default rate).
   */
  getEffectiveTotalPrice(): number {
    if (!this.heldSlot) return 0;
    // Use multi-player calculated price when:
    // - Per-player pricing is enabled (always uses multi-player calculation)
    // - OR when we have selected players
    if (
      this.multiPlayerPricePreview &&
      (this.isPricingPerPlayerEnabled() ||
        this.selectedPlayers.length > 0 ||
        this.getGuestSlotCount() > 0)
    ) {
      return this.multiPlayerPricePreview.totalPrice;
    }
    return this.heldSlot.price;
  }

  /**
   * Get the remaining amount after vouchers
   */
  getRemainingAmount(): number {
    if (!this.heldSlot) return 0;
    if (this.voucherCalculation) {
      return this.voucherCalculation.remainingAmount;
    }
    return this.getEffectiveTotalPrice();
  }

  /**
   * Get min players for category
   */
  getMinPlayers(): number {
    return (this.heldSlot?.category as { minPlayers?: number })?.minPlayers ?? 1;
  }

  /**
   * Get max players for category
   */
  getMaxPlayers(): number {
    return (this.heldSlot?.category as { maxPlayers?: number })?.maxPlayers ?? 4;
  }

  /**
   * Get the number of guest slots.
   * Computed from minPlayers + extraGuestSlots (tracked via player-selector events).
   */
  getGuestSlotCount(): number {
    // Guest slots exist in per-player mode AND in booker-pays categories
    // with a matching guest allowance (member-only windows).
    if (!this.isPricingPerPlayerEnabled() && !this.hasGuestAllowance()) {
      return 0;
    }
    const minPlayers = this.getMinPlayers();
    const maxPlayers = this.getMaxPlayers();
    const filledSlots = 1 + this.selectedPlayers.length;
    const requiredGuests = Math.max(0, minPlayers - filledSlots);
    const extraGuests = Math.max(0, this.extraGuestSlots);
    return Math.min(requiredGuests + extraGuests, maxPlayers - filledSlots);
  }

  getPlayerUserIds(): string[] {
    return this.selectedPlayers.map(p => p.id);
  }

  onGuestSlotCountChanged(count: number): void {
    // Derive extraGuestSlots from the emitted count
    const minPlayers = this.getMinPlayers();
    const filledSlots = 1 + this.selectedPlayers.length;
    const requiredGuests = Math.max(0, minPlayers - filledSlots);
    this.extraGuestSlots = Math.max(0, count - requiredGuests);
  }

  formatTime(time: string): string {
    return this.timeUtils.formatTimeForDisplay(time);
  }

  // ============================================================================
  // Helper Methods for Template
  // ============================================================================

  getOrganizationId(): string | undefined {
    return (this.heldSlot?.category as { organizationId?: string })
      ?.organizationId;
  }

  private loadOrganizationSettings(organizationId: string): void {
    this.subscriptions.add(
      this.organizationSettingsService.getPublicSettings(organizationId).subscribe((settings) => {
        this.onSitePaymentEnabled = settings.payment.onSitePaymentEnabled;
        this.stripePaymentEnabled = settings.payment.stripePaymentEnabled;
        this.voucherPaymentEnabled = settings.payment.voucherPaymentEnabled;
        this.sepaPaymentEnabled = settings.payment.sepaPaymentEnabled;
        this.paypalPaymentEnabled = settings.payment.paypalPaymentEnabled;
        this.paypalClientId = settings.payment.paypalClientId;
        this.paypalEnvironment = settings.payment.paypalEnvironment;
      })
    );
  }

  getCategoryId(): string | undefined {
    return (this.heldSlot?.category as { id?: string })?.id;
  }

  getPaymentDescription(): string {
    if (!this.heldSlot) return '';
    const dateStr = new Date(this.heldSlot.date).toLocaleDateString('de-DE', {
      month: 'short',
      day: 'numeric',
    });
    return `Platzbuchung am ${dateStr} um ${this.formatTime(this.heldSlot.startTime)}`;
  }

  isFullyCoveredByVouchers(): boolean {
    return this.isFullyCovered;
  }

  /**
   * A 0,00 € booking (e.g. a member-free slot without guest fees). There is
   * nothing to collect, so no payment method is needed: payment-section does
   * not even mount payment-options at a zero amount, so no method is ever
   * reported. Submitted as on-site — the backend settles any zero-amount
   * booking as COMPLETED and skips the on-site gate for it, so this does not
   * depend on the club offering on-site payment.
   */
  nothingToCharge(): boolean {
    return !!this.heldSlot && Number(this.getEffectiveTotalPrice()) <= 0;
  }

  /**
   * Determines if the confirm button should be disabled.
   * Button is disabled when:
   * - Booking is in progress
   * - No payment method reported yet (e.g. SEPA mandate still loading)
   * - Card payment selected but form not complete or confirm not ready
   * - No payment methods available AND vouchers don't fully cover the price
   */
  isConfirmButtonDisabled(): boolean {
    // Always disabled during processing
    if (this.bookingInProgress) {
      return true;
    }

    // A voucher tick is still being validated: submitting now would send the
    // unchecked code. Checked before the full-cover shortcut, because the
    // pending result can change whether vouchers cover the price at all.
    if (this.voucherCalculationPending()) {
      return true;
    }

    // If fully covered by vouchers, always allow submission
    if (this.isFullyCoveredByVouchers()) {
      return false;
    }

    // Nothing to charge — no method needs to be chosen, enabled or completed.
    if (this.nothingToCharge()) {
      return false;
    }

    // Payment-options has not decided on a method yet — there is nothing to
    // submit with.
    const method = this.paymentMethod();
    if (method === null) {
      return true;
    }

    // If card payment selected but form not complete or confirm not ready
    if (method === 'card' && (!this.stripeFormComplete() || !this.stripeConfirmFn())) {
      return true;
    }

    // If no payment methods available (voucher-only mode) but vouchers don't cover full amount.
    // SEPA is only selectable when the member has a usable mandate (guarded in payment-options),
    // so a 'sepa' selection means a valid method is available even when on-site/Stripe are off.
    if (
      !this.onSitePaymentEnabled &&
      !this.stripePaymentEnabled &&
      method !== 'sepa'
    ) {
      return true;
    }

    return false;
  }

  // ============================================================================
  // Form Submission
  // ============================================================================

  async onSubmit(): Promise<void> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      this.bookingError = 'Benutzer nicht authentifiziert';
      return;
    }

    if (!this.heldSlot) {
      this.bookingError = 'Keine aktive Reservierung zum Bestätigen';
      return;
    }

    // Handle voucher-only payments
    if (this.isFullyCoveredByVouchers()) {
      this.processUnifiedBooking(PaymentMethod.VOUCHER);
      return;
    }

    // Free booking: no method was (or could be) chosen — see nothingToCharge().
    if (this.nothingToCharge()) {
      this.processUnifiedBooking(PaymentMethod.ON_SITE);
      return;
    }

    const method = this.paymentMethod();

    // Handle card payment: trigger Stripe confirm, then booking completes via onPaymentSuccess
    if (method === 'card') {
      const confirmFn = this.stripeConfirmFn();
      if (!confirmFn) {
        this.bookingError = 'Zahlungsformular nicht bereit';
        return;
      }
      this.bookingInProgress = true;
      this.bookingError = null;
      await confirmFn();
      // Result flows back via onPaymentSuccess() or onPaymentError()
      return;
    }

    // Handle SEPA direct debit: confirmed on trust, collected later in a batch (no Stripe confirm)
    if (method === 'sepa') {
      this.processUnifiedBooking(PaymentMethod.SEPA_DEBIT);
      return;
    }

    // Process cash/on-site payment
    if (method === 'cash') {
      this.processUnifiedBooking(PaymentMethod.ON_SITE);
    }
    // null: no method decided yet (the button is disabled then). 'paypal':
    // the PayPal button owns the submit and the booking is created by its
    // capture (onPaymentSuccess). Neither may be booked as on-site payment.
  }

  private processUnifiedBooking(paymentMethod: PaymentMethod): void {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !this.heldSlot) {
      this.bookingError = 'Erforderliche Buchungsinformationen fehlen';
      return;
    }

    this.bookingInProgress = true;
    this.bookingError = null;

    try {
      const bookingData: CreateBookingRequest = {
        court_id: this.heldSlot.court.id,
        user_id: currentUser.userId,
        start_time: this.heldSlot.startTime,
        end_time: this.heldSlot.endTime,
        duration_minutes: this.heldSlot.durationMinutes,
        price: this.getEffectiveTotalPrice(),
        hold_id: this.heldSlot.id,
        client_time_zone: this.timeUtils.getClientTimeZone(),
        payment_method: paymentMethod,
        voucher_codes:
          this.selectedVoucherCodes.length > 0
            ? this.selectedVoucherCodes
            : undefined,
        player_user_ids:
          this.selectedPlayers.length > 0
            ? this.selectedPlayers.map(p => p.id)
            : undefined,
        guest_slot_count:
          this.getGuestSlotCount() > 0 ? this.getGuestSlotCount() : undefined,
      };

      this.bookingApiService.confirmBooking(bookingData).subscribe({
        next: () => {
          this.bookingInProgress = false;
          this.holdReleased = true;

          if (paymentMethod === PaymentMethod.VOUCHER) {
            this.toastService.success('Buchung mit Gutscheinen bestätigt!');
          } else if (this.selectedVoucherCodes.length > 0) {
            this.toastService.success(
              'Buchung mit teilweiser Gutscheinzahlung bestätigt!'
            );
          } else if (paymentMethod === PaymentMethod.SEPA_DEBIT) {
            // SEPA is collected later in a batch — set the expectation explicitly.
            this.toastService.success(
              'Buchung bestätigt! Der Betrag wird per SEPA-Lastschrift eingezogen.'
            );
          } else {
            this.toastService.success('Buchung erfolgreich bestätigt!');
          }

          // Capture booking data before transitioning to success
          this.confirmedBooking = {
            categoryName: this.heldSlot!.category.name,
            courtName: this.heldSlot!.court.name,
            date: this.heldSlot!.date,
            startTime: this.heldSlot!.startTime,
            price: this.getEffectiveTotalPrice(),
            players: this.selectedPlayers.length > 0 ? [...this.selectedPlayers] : undefined,
          };
          this.step.set('success');
          this.bookingConfirmed.emit();
        },
        error: (error: unknown) => {
          this.bookingInProgress = false;
          this.bookingError = this.errorHandlingService.getGeneralErrorMessage(error);
          this.toastService.error(`Buchung fehlgeschlagen: ${this.bookingError}`);
        },
      });
    } catch (error: unknown) {
      this.bookingInProgress = false;
      this.bookingError =
        error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten';
    }
  }

  // ============================================================================
  // Success Screen Actions
  // ============================================================================

  goToMyBookings(): void {
    this.holdReleased = true;
    this.close.emit();
    this.modalClosed.emit();
    this.router.navigate(['/my-bookings']);
  }

  bookAnother(): void {
    this.holdReleased = true;
    this.close.emit();
    this.modalClosed.emit();
  }

  formatDateForSuccess(date: string | Date): string {
    return formatDateLongGerman(date);
  }

  onBackdropClick(): void {
    if (this.step() === 'success') {
      this.bookAnother();
    } else {
      this.onCancel();
    }
  }
}
